import React, {useCallback, useEffect, useState} from "react";
import {Link as RouterLink, useParams} from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    Typography,
} from "@mui/material";
import {useUserAuth} from "../../context/UserAuthContext";
import {getProject} from "../../services/associationService";
import {ProjectAvatar} from "../common/ProjectImage";
import {
    applyToProject,
    getApplicationState,
    leaveApprovedProject,
    removeMyApplication,
} from "../../services/engineerService";
import PortalTopbar from "../Association/PortalTopbar";

const PRIMARY = "#3E4F88";

/**
 * Detalhe de projeto — vista do engenheiro (Fase 2).
 * Espelha o ProjectDetailsPage da app ESFAPP com os mesmos 3 estados:
 *   - ainda não candidato -> "Candidatar-se" (verde)
 *   - candidatura pendente -> "Remover candidatura" (vermelho)
 *   - projeto aprovado    -> "Sair do projeto" (vermelho)
 * Cada ação grava/apaga em Applications/ApprovedApplications e envia email
 * à associação (a app usava push + email; no browser só email).
 */
const EngineerProjectDetails = () => {

    const {id} = useParams();
    const {profile} = useUserAuth();

    const [project, setProject] = useState(null);
    const [status, setStatus] = useState(null); // "applied" | "approved" | null
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [actionError, setActionError] = useState("");
    const [success, setSuccess] = useState("");
    const [busy, setBusy] = useState(false);
    const [confirm, setConfirm] = useState(null); // {title, text, confirmLabel, danger, onConfirm}

    const load = useCallback(async () => {
        try {
            const p = await getProject(id);
            setProject(p);
            if (p && p.Id && profile) {
                const state = await getApplicationState(p.Id, profile);
                setStatus(state);
            }
            setError("");
        } catch (err) {
            console.error("Erro ao carregar projeto:", err);
            setError("Não foi possível carregar o projeto.");
        } finally {
            setLoading(false);
        }
    }, [id, profile]);

    useEffect(() => {
        load();
    }, [load]);

    // O registo a gravar/apagar é o do engenheiro (sem o "key" interno do nó RTDB)
    const engineer = profile ? Object.fromEntries(Object.entries(profile).filter(([k]) => k !== "key")) : null;

    const runConfirm = (title, text, confirmLabel, danger, onConfirm) => {
        setActionError("");
        setSuccess("");
        setConfirm({title, text, confirmLabel, danger, onConfirm});
    };

    const handleApply = () => {
        runConfirm(
            "Candidatar-se",
            "Deseja candidatar-se a este projeto? A associação será notificada por email.",
            "Candidatar-se",
            false,
            async () => {
                try {
                    await applyToProject(project, engineer);
                    setStatus("applied");
                    setSuccess("Candidatura submetida! A associação foi notificada e irá analisar a tua candidatura.");
                } catch (err) {
                    console.error(err);
                    setActionError("Não foi possível candidatar-se. Tenta novamente.");
                }
            }
        );
    };

    const handleRemoveApplication = () => {
        runConfirm(
            "Remover candidatura",
            "Deseja remover a sua candidatura a este projeto?",
            "Remover",
            true,
            async () => {
                try {
                    await removeMyApplication(project, engineer);
                    setStatus(null);
                    setSuccess("Candidatura removida. A associação foi notificada.");
                } catch (err) {
                    console.error(err);
                    setActionError("Não foi possível remover a candidatura. Tenta novamente.");
                }
            }
        );
    };

    const handleLeaveApproved = () => {
        runConfirm(
            "Sair do projeto",
            "Deseja sair deste projeto? A associação será notificada para gerir as vagas.",
            "Sair",
            true,
            async () => {
                try {
                    await leaveApprovedProject(project, engineer);
                    setStatus(null);
                    setSuccess("Saíste do projeto. A associação foi notificada.");
                } catch (err) {
                    console.error(err);
                    setActionError("Não foi possível sair do projeto. Tenta novamente.");
                }
            }
        );
    };

    // ---------- estados de carregamento / erro ----------
    if (loading) {
        return (
            <Box minHeight="100vh" sx={{bgcolor: "#f4f5fa"}}>
                <PortalTopbar/>
                <Box sx={{display: "flex", justifyContent: "center", py: 10}}>
                    <CircularProgress/>
                </Box>
            </Box>
        );
    }

    if (!project) {
        return (
            <Box minHeight="100vh" sx={{bgcolor: "#f4f5fa"}}>
                <PortalTopbar/>
                <Container maxWidth="sm" sx={{py: 8}}>
                    <Stack spacing={3} alignItems="center">
                        <Alert severity={error ? "error" : "warning"}>
                            {error || "Projeto não encontrado. Talvez já tenha sido removido."}
                        </Alert>
                        <Button component={RouterLink} to="/app/engenheiro" variant="contained" sx={{bgcolor: PRIMARY, fontWeight: 600}}>
                            Voltar à lista
                        </Button>
                    </Stack>
                </Container>
            </Box>
        );
    }

    // ---------- página ----------
    return (
        <Box minHeight="100vh" sx={{bgcolor: "#f4f5fa"}}>
            <PortalTopbar/>
            <Container maxWidth="lg" sx={{py: 5}}>
                <Stack spacing={3}>
                    <Button component={RouterLink} to="/app/engenheiro" variant="text" sx={{color: PRIMARY, fontWeight: 600, alignSelf: "flex-start"}}>
                        ← Voltar à lista de projetos
                    </Button>

                    {actionError && <Alert severity="error">{actionError}</Alert>}
                    {success && <Alert severity="success">{success}</Alert>}

                    {/* Cartão do projeto */}
                    <Box sx={{bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, p: 3, display: "flex", gap: 3, flexDirection: {xs: "column", sm: "row"}, alignItems: {sm: "center"}}}>
                        <ProjectAvatar src={project.Logo} alt={project.Title} size={88}/>
                        <Box sx={{flex: 1}}>
                            <Typography variant="h4" sx={{fontWeight: 700, color: PRIMARY, mb: 1}}>
                                {project.Title}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{mb: 1.5}}>
                                {project.Association}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{mb: 1.5}}>
                                <Chip label={`🎓 ${project.College}`} size="small"/>
                                <Chip label={`📍 ${project.Location}`} size="small"/>
                                <Chip label={project.Time || "—"} size="small"/>
                                <Chip label={project.Type || "—"} size="small"/>
                            </Stack>
                            {project.Disc ? (
                                <Typography variant="body2" color="text.secondary" sx={{whiteSpace: "pre-line"}}>
                                    {project.Disc}
                                </Typography>
                            ) : (
                                <Typography variant="body2" color="text.disabled">Sem descrição.</Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Ação (mesmos estados da app) */}
                    <Box sx={{bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, p: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5}}>
                        {status === "applied" ? (
                            <>
                                <Typography variant="body2" color="text.secondary" sx={{textAlign: "center"}}>
                                    A tua candidatura está a ser analisada pela associação.
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="large"
                                    color="error"
                                    disabled={busy}
                                    onClick={handleRemoveApplication}
                                    sx={{fontWeight: 600, px: 4}}
                                >
                                    Remover candidatura
                                </Button>
                            </>
                        ) : status === "approved" ? (
                            <>
                                <Typography variant="body2" color="text.secondary" sx={{textAlign: "center"}}>
                                    Foste aprovado para este projeto. A associação entrará em contacto contigo.
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="large"
                                    color="error"
                                    disabled={busy}
                                    onClick={handleLeaveApproved}
                                    sx={{fontWeight: 600, px: 4}}
                                >
                                    Sair do projeto
                                </Button>
                            </>
                        ) : (
                            <>
                                <Typography variant="body2" color="text.secondary" sx={{textAlign: "center"}}>
                                    Candidata-te a este projeto de voluntariado.
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="large"
                                    disabled={busy}
                                    onClick={handleApply}
                                    sx={{bgcolor: "#2e7d32", "&:hover": {bgcolor: "#1b5e20"}, fontWeight: 600, px: 4}}
                                >
                                    Candidatar-se
                                </Button>
                            </>
                        )}
                    </Box>
                </Stack>
            </Container>

            {/* Confirmação (igual ao padrão do portal de associações) */}
            {confirm && (
                <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
                    <DialogTitle>{confirm.title}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>{confirm.text}</DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{px: 3, pb: 2}}>
                        <Button onClick={() => setConfirm(null)} color="inherit">
                            Cancelar
                        </Button>
                        <Button
                            onClick={async () => {
                                const c = confirm;
                                setConfirm(null);
                                setBusy(true);
                                try {
                                    await c.onConfirm();
                                } finally {
                                    setBusy(false);
                                }
                            }}
                            variant="contained"
                            color={confirm.danger ? "error" : "primary"}
                            sx={confirm.danger ? {} : {bgcolor: PRIMARY, "&:hover": {bgcolor: "#2c3a68"}}}
                        >
                            {confirm.confirmLabel}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    );
};

export default EngineerProjectDetails;
